import { z } from "zod";
import {
  CRAWL_RUN_STATUS,
  CrawlFailureReasonSchema,
  CrawlFrontierStatusSchema,
  CrawlRunStatusSchema,
  IssueSeveritySchema,
} from "../../enums/core";
import {
  YeetAttemptFailureReasonSchema,
  YeetAttemptStatusSchema,
  YeetFetchStatusSchema,
} from "../../enums/yeet";

// Read surface over workers.crawl_run and friends: how the frontend sees a
// scan while it runs and after it lands. Derived server-side so every client
// observes the same state; no columns are added for it.

export const RunPageCountsSchema = z.object({
  pending: z.number().int().min(0),
  inFlight: z.number().int().min(0),
  scraped: z.number().int().min(0),
  failed: z.number().int().min(0),
  skipped: z.number().int().min(0),
  // The subset of `failed` that failed at analysis rather than at fetch. A
  // fetch failure fails the frontier row too, so without this the same failure
  // is reported once per stage.
  analysisFailed: z.number().int().min(0),
});

export type RunPageCounts = z.infer<typeof RunPageCountsSchema>;

// The fetch hop between crawl admission and sandbox analysis. Without these a
// waiting page is indistinguishable from a page waiting on a sandbox, which is
// the difference between "the origin is slow" and "no worker is consuming".
// `analysisDispatched` is timestamp presence, not status: it counts the fetches
// actually handed to a sandbox, which is the analyse stage's real total.
export const RunFetchCountsSchema = z.object({
  pending: z.number().int().min(0),
  ready: z.number().int().min(0),
  failed: z.number().int().min(0),
  analysisDispatched: z.number().int().min(0),
});

export type RunFetchCounts = z.infer<typeof RunFetchCountsSchema>;

export const RunSummarySchema = z.object({
  runId: z.uuid(),
  websiteId: z.uuid(),
  status: CrawlRunStatusSchema,
  startedAt: z.coerce.date(),
  finishedAt: z.coerce.date().nullable(),
  error: z.string().nullable(),
  maxPages: z.number().int(),
  // How many of the run's pages have been re-analysed at least once, and how
  // many job generations the run has produced (1 = never repaired).
  repairedPageCount: z.number().int().min(0),
  generationCount: z.number().int().min(0),
  pageCounts: RunPageCountsSchema,
  fetchCounts: RunFetchCountsSchema,
  // The run has drained its frontier and claimed canonicalization but has not
  // landed yet. Without this a run wedged in the canonicalize hop is
  // indistinguishable from one still analysing pages.
  canonicalizing: z.boolean(),
});

export type RunSummary = z.infer<typeof RunSummarySchema>;

/**
 * A run that has not started yet: the optimistic placeholder the frontend
 * writes on scan start, before the backend has minted the real run. Lives here
 * so adding a field to the schema cannot leave a hand-mirrored literal stale.
 */
export function emptyRunSummary(websiteId: string): RunSummary {
  return {
    // A real uuid so the placeholder is a valid RunSummary and can key a list;
    // it is replaced the moment the backend's run lands.
    runId: crypto.randomUUID(),
    websiteId,
    status: CRAWL_RUN_STATUS.RUNNING,
    startedAt: new Date(),
    finishedAt: null,
    error: null,
    // Zero is the placeholder's tell: readers use it to render "starting scan"
    // rather than a 0-of-0 progress reading.
    maxPages: 0,
    repairedPageCount: 0,
    generationCount: 0,
    pageCounts: {
      pending: 0,
      inFlight: 0,
      scraped: 0,
      failed: 0,
      skipped: 0,
      analysisFailed: 0,
    },
    fetchCounts: { pending: 0, ready: 0, failed: 0, analysisDispatched: 0 },
    canonicalizing: false,
  };
}

// Live liveness of the yeet dispatcher fleet, read from the yeet queue while a
// run is running: `consumers` is how many dispatchers are consuming, `queued`
// how many page jobs are waiting.
export const YeetWorkerStatusSchema = z.object({
  consumers: z.number().int().min(0),
  queued: z.number().int().min(0),
});
export type YeetWorkerStatus = z.infer<typeof YeetWorkerStatusSchema>;

// The "not picked up" state: the scan crawled and fetched but no dispatcher is
// consuming, so queued pages are stuck rather than actively being analysed.
// The single definition every run reader (scan bar, playground, run detail)
// shares so the predicate never drifts.
export function isYeetWorkerStalled(
  worker: YeetWorkerStatus | null,
): worker is YeetWorkerStatus {
  return worker !== null && worker.consumers === 0 && worker.queued > 0;
}

export const CurrentRunResponseSchema = z.object({
  // Latest run while it is still running; null once it has landed. The frontend
  // polls this while non-null.
  run: RunSummarySchema.nullable(),
  // Yeet fleet liveness while the run is running; null once it has landed.
  yeetWorker: YeetWorkerStatusSchema.nullable(),
});

export type CurrentRunResponse = z.infer<typeof CurrentRunResponseSchema>;

export const RunListResponseSchema = z.object({
  runs: z.array(RunSummarySchema),
});

export type RunListResponse = z.infer<typeof RunListResponseSchema>;

export const RunPageSchema = z.object({
  url: z.string(),
  depth: z.number().int().min(0),
  status: CrawlFrontierStatusSchema,
  failureReason: CrawlFailureReasonSchema.nullable(),
  attempts: z.number().int().min(0),
  lastAttemptAt: z.coerce.date(),
  lastError: z.string().nullable(),
  // Null until a sandbox attempt exists for this page.
  attemptStatus: YeetAttemptStatusSchema.nullable(),
  // Why the latest attempt failed; null while running, on success, or before an
  // attempt exists.
  attemptFailureReason: YeetAttemptFailureReasonSchema.nullable(),
  attemptSummary: z.string().nullable(),
  attemptFinishedAt: z.coerce.date().nullable(),
  // Fetch hop for this page. Null when no fetch row exists yet.
  fetchStatus: YeetFetchStatusSchema.nullable(),
  // Origin status code once the fetch is ready; null while pending or failed.
  httpStatus: z.number().int().nullable(),
});

export type RunPage = z.infer<typeof RunPageSchema>;

export const RunFindingSchema = z.object({
  id: z.uuid(),
  title: z.string(),
  detail: z.string(),
  severity: IssueSeveritySchema,
  url: z.string(),
  metricKey: z.string(),
  recommendedAction: z.string().nullable(),
  createdAt: z.coerce.date(),
});

export type RunFinding = z.infer<typeof RunFindingSchema>;

// Where the next page of frontier rows starts. The page list is ordered by
// (depth, url), which is stable and unique per run, so it doubles as the
// cursor. Null means this is the last page.
export const RunPageCursorSchema = z.object({
  depth: z.number().int().min(0),
  url: z.string(),
});

export type RunPageCursor = z.infer<typeof RunPageCursorSchema>;

export const RunDetailResponseSchema = RunSummarySchema.extend({
  pages: z.array(RunPageSchema),
  // Null on the last page. A client that wants the rest asks for it, rather
  // than being told its list is incomplete with no way to complete it.
  nextPageCursor: RunPageCursorSchema.nullable(),
  // Raw agent findings as they land during a run (pre-consolidation), newest
  // first, capped server-side.
  findings: z.array(RunFindingSchema),
  findingCounts: z.object({
    critical: z.number().int().min(0),
    high: z.number().int().min(0),
    medium: z.number().int().min(0),
    low: z.number().int().min(0),
  }),
});

export type RunDetailResponse = z.infer<typeof RunDetailResponseSchema>;
